import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import Button from './Button';

const instancia = process.env.REACT_APP_INSTANCIA;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled(motion.div)`
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  color: #333;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  transition: color 0.3s;
  
  &:hover {
    color: #e74c3c;
  }
`;

const ModalBody = styled.div`
  margin-bottom: 20px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const InfoItem = styled.div`
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: #666;
  font-weight: 500;
  margin-right: 8px;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: #333;
`;

const TextAreaWrapper = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #6c5ce7;
    box-shadow: 0 0 0 2px rgba(108, 92, 231, 0.1);
  }
`;

const NotaModal = ({
  isOpen,
  onClose,
  notaInfo,
  operacao
}) => {
  const [justificativa, setJustificativa] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [erro, setErro] = useState('');

  // Reset do estado quando a modal é aberta
  useEffect(() => {
    if (isOpen) {
      setJustificativa('');
      setErro('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!justificativa.trim()) {
      setErro('Por favor, informe uma justificativa.');
      return;
    }

    setIsLoading(true);
    setErro('');

    try {

      const obj = {
        ref: notaInfo.numero,
        justificativa: justificativa,
        cod_pedido_oficial: notaInfo.pedidoId,
        instancia: `${instancia}`,
        producao: process.env.REACT_APP_PRODUCAO
      }
      let retorno;
      if (operacao === 'cancelar') {
        retorno = await api.post('/notas/cancelarDocumento', obj);
        if (retorno?.data?.status_sefaz !== "135") {
          console.error(`Erro ao ${operacao === 'cancelar' ? 'cancelar' : 'enviar carta de correção para'} a nota:`, retorno);
          setErro(`Ocorreu um erro ao processar sua solicitação. ${JSON.stringify(retorno?.data) || ''}`);
        } else {
          alert(`Operação de ${operacao === 'cancelar' ? 'cancelamento' : 'carta de correção'} enviada com sucesso!`);
          onClose();
        }
      } else if (operacao === 'carta') {
        retorno = await api.post('/notas/cartaoCorrecaoNota', obj);
        if (retorno?.data?.status_sefaz !== "135") {
          console.error(`Erro ao ${operacao === 'cancelar' ? 'cancelar' : 'enviar carta de correção para'} a nota:`, retorno);
          setErro(`Ocorreu um erro ao processar sua solicitação. ${retorno?.data || ''}`);
        } else {
          alert(`Operação de ${operacao === 'cancelar' ? 'cancelamento' : 'carta de correção'} enviada com sucesso!`);
          onClose();
        }
      }

    } catch (error) {
      console.error(`Erro ao ${operacao === 'cancelar' ? 'cancelar' : 'enviar carta de correção para'} a nota:`, JSON.stringify(error));
      setErro(`Ocorreu um erro ao processar sua solicitação. ${error.response?.data?.mensagem || ''}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Título dinâmico baseado na operação
  const titulo = operacao === 'cancelar'
    ? 'Cancelar Nota Fiscal'
    : 'Carta de Correção';

  // Placeholder dinâmico para o textarea
  const placeholder = operacao === 'cancelar'
    ? 'Informe o motivo para o cancelamento da nota fiscal...'
    : 'Descreva as correções a serem realizadas na nota fiscal...';

  // Label dinâmico para o textarea
  const label = operacao === 'cancelar'
    ? 'Justificativa para Cancelamento'
    : 'Texto da Carta de Correção';

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContainer
            onClick={(e) => e.stopPropagation()}
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: 'spring', damping: 25 }}
          >
            <ModalHeader>
              <ModalTitle>{titulo}</ModalTitle>
              <CloseButton onClick={onClose}>&times;</CloseButton>
            </ModalHeader>

            <ModalBody>
              {/* Informações da nota */}
              <InfoItem>
                <InfoLabel>Pedido:</InfoLabel>
                <InfoValue>{notaInfo?.pedidoId || '-'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Número da Nota:</InfoLabel>
                <InfoValue>{notaInfo?.numero || '-'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Cliente:</InfoLabel>
                <InfoValue>{notaInfo?.cliente || '-'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Data de Emissão:</InfoLabel>
                <InfoValue>{notaInfo?.emissao || '-'}</InfoValue>
              </InfoItem>

              {/* Campo de justificativa */}
              <TextAreaWrapper>
                <Label htmlFor="justificativa">{label}</Label>
                <TextArea
                  id="justificativa"
                  value={justificativa}
                  onChange={(e) => setJustificativa(e.target.value)}
                  placeholder={placeholder}
                />
              </TextAreaWrapper>

              {erro && (
                <div style={{ color: '#e74c3c', fontSize: '14px', marginTop: '8px' }}>
                  {erro}
                </div>
              )}
            </ModalBody>

            <ModalFooter>
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                isLoading={isLoading}
              >
                {operacao === 'cancelar' ? 'Solicitar Cancelamento' : 'Enviar Correção'}
              </Button>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default NotaModal;